import { WordsListenedEvent } from 'modules/sdk/lib';
import type { EmbeddedListeningDependencies, ListeningDependencies } from 'modules/sdk/listeningDependencies';
import { tryQueueCsatBanner } from 'modules/sideBanner/stores/csatActions';

import { subscriptionStoreSelectors } from '../selectors';
import { switchToFree } from './switchToFree';

// every word is considered 5 characters long, so 200 characters is 40 words
const CHARACTER_COUNT_THRESHOLD = 200;

let cleanUpPreviousWordTracker: () => void = () => {};

const getCurrentCount = () => Number.parseInt(localStorage.getItem('currentCharCount') ?? '0', 10);

const updateCurrentCount = (count: number) => {
  localStorage.setItem('currentCharCount', count.toString());
};

const handleWordsListened = (event: WordsListenedEvent, lastCount: number, currentCount: number) => {
  if (!event) return { lastCount, currentCount };

  const { charCount, voice } = event;
  const charCountDiff = Math.abs(charCount - lastCount);

  if (currentCount > 1_000) {
    currentCount = 0;
    updateCurrentCount(0);
  }

  if (charCountDiff >= CHARACTER_COUNT_THRESHOLD) {
    lastCount = charCount;
    currentCount += CHARACTER_COUNT_THRESHOLD;
    updateCurrentCount(currentCount);
  }

  tryQueueCsatBanner(currentCount);

  // Switch to free if using premium voice without premium voice access
  if (voice.isPremium && !subscriptionStoreSelectors.getHasAccessToPremiumVoice()) {
    switchToFree();
  }

  return { lastCount, currentCount };
};

export const startWordTracker = (listeningDependencies: ListeningDependencies) => {
  cleanUpPreviousWordTracker();
  let lastCount = 0;
  let currentCount = getCurrentCount();

  cleanUpPreviousWordTracker = listeningDependencies.playbackInfo.addWordsListenedListener(event => {
    const result = handleWordsListened(event, lastCount, currentCount);
    lastCount = result.lastCount;
    currentCount = result.currentCount;
  });
};

// This is part of closure to ensure lastCount and currentCount persisting between
// invokations in multiple places.
const createAlsoTrackWordsFor = () => {
  if (typeof window === 'undefined') return () => {};

  let lastCount = 0;
  let currentCount = getCurrentCount();
  let cleanupFunctions: (() => void)[] = [];

  return (listeningDependencies: ListeningDependencies | EmbeddedListeningDependencies) => {
    const cleanup = listeningDependencies.playbackInfo.addWordsListenedListener(event => {
      const result = handleWordsListened(event, lastCount, currentCount);
      lastCount = result.lastCount;
      currentCount = result.currentCount;
    });

    cleanupFunctions.push(cleanup);

    return () => {
      cleanup();
      cleanupFunctions = cleanupFunctions.filter(fn => fn !== cleanup);
    };
  };
};

export const alsoTrackWordsFor = createAlsoTrackWordsFor();
